/**
 * LocalStorageService - Typed localStorage access for Idle Depths.
 *
 * Provides a type-safe wrapper around browser localStorage with:
 * - Namespaced keys to avoid conflicts
 * - JSON serialization/deserialization
 * - Error handling for quota exceeded and parse errors
 * - Version tracking for schema migrations
 */

import {
  type CharacterData,
  type GuestData,
  type UserPreferences,
  DEFAULT_PREFERENCES,
  STARTING_CURRENCY,
  createEmptyEquipment,
  validatePreferences,
  isValidPreferences,
  validateCharacterData,
} from '@idle-depths/shared';

/**
 * Current storage schema version.
 * Increment when making breaking changes to storage structure.
 *
 * Version history:
 * - v1: Initial version (guest data, preferences)
 * - v2: Added character storage
 * - v3: Added currency to characters (v0.2.3)
 * - v4: Added equipment to characters (v0.2.3)
 */
const STORAGE_VERSION = 4;

/**
 * Storage key prefix to namespace our data.
 */
const KEY_PREFIX = 'idle-depths:';

/**
 * All localStorage keys used by the application.
 */
export const STORAGE_KEYS = {
  /** Guest data (guestId, character, preferences) */
  GUEST_DATA: `${KEY_PREFIX}guest`,

  /** User preferences (synced separately for quick access) */
  PREFERENCES: `${KEY_PREFIX}preferences`,

  /** Storage schema version */
  VERSION: `${KEY_PREFIX}version`,

  /** OAuth state for CSRF protection */
  OAUTH_STATE: `${KEY_PREFIX}oauth-state`,

  /** Server-side guest user ID for session recovery */
  GUEST_USER_ID: `${KEY_PREFIX}guest-user-id`,

  /** Character data array (primary game data) */
  CHARACTERS: `${KEY_PREFIX}characters`,

  /** Active character ID */
  ACTIVE_CHARACTER_ID: `${KEY_PREFIX}active-character`,

  /** Last tick timestamp for offline progress calculation */
  LAST_TICK_TIME: `${KEY_PREFIX}last-tick`,
} as const;

/**
 * LocalStorageService provides typed access to browser localStorage.
 *
 * All methods are static since localStorage is a singleton.
 * Handles JSON serialization and error cases gracefully.
 */
// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class LocalStorageService {
  // ===========================================================================
  // Version Management
  // ===========================================================================

  /**
   * Gets the current storage version.
   *
   * @returns Current version number or 0 if not set
   */
  static getVersion(): number {
    try {
      const version = localStorage.getItem(STORAGE_KEYS.VERSION);
      return version ? parseInt(version, 10) : 0;
    } catch {
      return 0;
    }
  }

  /**
   * Sets the storage version.
   *
   * @param version - Version number to set
   */
  static setVersion(version: number): void {
    try {
      localStorage.setItem(STORAGE_KEYS.VERSION, version.toString());
    } catch (error) {
      console.error('Failed to set storage version:', error);
    }
  }

  /**
   * Checks if storage needs migration and performs it if needed.
   */
  static migrateIfNeeded(): void {
    const currentVersion = this.getVersion();

    if (currentVersion < STORAGE_VERSION) {
      this.performMigration(currentVersion, STORAGE_VERSION);
      this.setVersion(STORAGE_VERSION);
    }
  }

  /**
   * Performs migration between storage versions.
   *
   * @param fromVersion - Current storage version
   * @param toVersion - Target storage version
   */
  private static performMigration(fromVersion: number, toVersion: number): void {
    // eslint-disable-next-line no-console
    console.info(`Migrating localStorage from v${String(fromVersion)} to v${String(toVersion)}`);

    // Migration from v2 to v3: Add currency to characters
    if (fromVersion < 3) {
      this.migrateV2ToV3();
    }

    // Migration from v3 to v4: Add equipment to characters
    if (fromVersion < 4) {
      this.migrateV3ToV4();
    }
  }

  /**
   * Migrate from v2 to v3: Add currency field to all characters.
   *
   * Characters created before v0.2.3 don't have a currency field.
   * This migration adds the default starting currency to existing characters.
   */
  private static migrateV2ToV3(): void {
    // eslint-disable-next-line no-console
    console.info('[LocalStorage] Migrating to v3: Adding currency to characters');

    const characters = this.getCharacters();
    if (characters.length === 0) {
      return;
    }

    const updated = characters.map((character) => ({
      ...character,
      currency: typeof character.currency === 'number' ? character.currency : STARTING_CURRENCY,
    }));

    this.setCharacters(updated);
    // eslint-disable-next-line no-console
    console.info(`[LocalStorage] Migrated ${String(characters.length)} characters to v3`);
  }

  /**
   * Migrate from v3 to v4: Add equipment field to all characters.
   *
   * Characters created before this version don't have an equipment field.
   * This migration adds empty equipment to existing characters.
   */
  private static migrateV3ToV4(): void {
    // eslint-disable-next-line no-console
    console.info('[LocalStorage] Migrating to v4: Adding equipment to characters');

    const characters = this.getCharacters();
    if (characters.length === 0) {
      return;
    }

    // Old characters may not have equipment field, so we use Partial type
    const updated = characters.map((character) => {
      const oldCharacter = character as Partial<CharacterData>;
      return {
        ...character,
        equipment: oldCharacter.equipment ?? createEmptyEquipment(),
      };
    });

    this.setCharacters(updated);
    // eslint-disable-next-line no-console
    console.info(`[LocalStorage] Migrated ${String(characters.length)} characters to v4`);
  }

  // ===========================================================================
  // Guest Data
  // ===========================================================================

  /**
   * Gets guest data from localStorage.
   *
   * @returns GuestData if exists and valid, null otherwise
   */
  static getGuestData(): GuestData | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.GUEST_DATA);
      if (!raw) {
        return null;
      }

      const parsed = JSON.parse(raw) as unknown;

      // Validate structure
      if (!this.isValidGuestData(parsed)) {
        console.warn('Invalid guest data in localStorage, clearing');
        this.clearGuestData();
        return null;
      }

      return parsed;
    } catch (error) {
      console.error('Failed to read guest data:', error);
      return null;
    }
  }

  /**
   * Saves guest data to localStorage.
   *
   * @param data - Guest data to save
   * @returns True if save succeeded, false otherwise
   */
  static setGuestData(data: GuestData): boolean {
    try {
      localStorage.setItem(STORAGE_KEYS.GUEST_DATA, JSON.stringify(data));
      return true;
    } catch (error) {
      console.error('Failed to save guest data:', error);
      return false;
    }
  }

  /**
   * Clears guest data from localStorage.
   */
  static clearGuestData(): void {
    try {
      localStorage.removeItem(STORAGE_KEYS.GUEST_DATA);
    } catch (error) {
      console.error('Failed to clear guest data:', error);
    }
  }

  /**
   * Checks if guest data exists in localStorage.
   *
   * @returns True if guest data exists
   */
  static hasGuestData(): boolean {
    return this.getGuestData() !== null;
  }

  /**
   * Validates that an object is valid GuestData.
   *
   * @param value - Value to validate
   * @returns True if value is valid GuestData
   */
  private static isValidGuestData(value: unknown): value is GuestData {
    if (typeof value !== 'object' || value === null) {
      return false;
    }

    const data = value as Record<string, unknown>;

    return (
      typeof data.guestId === 'string' &&
      data.guestId.length > 0 &&
      typeof data.createdAt === 'number' &&
      isValidPreferences(data.preferences)
    );
  }

  // ===========================================================================
  // Preferences
  // ===========================================================================

  /**
   * Gets user preferences from localStorage.
   *
   * @returns User preferences with defaults applied for missing fields
   */
  static getPreferences(): UserPreferences {
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.PREFERENCES);
      if (!raw) {
        return { ...DEFAULT_PREFERENCES };
      }

      const parsed = JSON.parse(raw) as unknown;

      if (!isValidPreferences(parsed)) {
        console.warn('Invalid preferences in localStorage, using defaults');
        return { ...DEFAULT_PREFERENCES };
      }

      return validatePreferences(parsed);
    } catch (error) {
      console.error('Failed to read preferences:', error);
      return { ...DEFAULT_PREFERENCES };
    }
  }

  /**
   * Saves user preferences to localStorage.
   *
   * @param preferences - Preferences to save
   * @returns True if save succeeded, false otherwise
   */
  static setPreferences(preferences: UserPreferences): boolean {
    try {
      localStorage.setItem(STORAGE_KEYS.PREFERENCES, JSON.stringify(preferences));
      return true;
    } catch (error) {
      console.error('Failed to save preferences:', error);
      return false;
    }
  }

  /**
   * Updates a single preference value.
   *
   * @param key - Preference key to update
   * @param value - New value
   * @returns True if save succeeded, false otherwise
   */
  static updatePreference<K extends keyof UserPreferences>(
    key: K,
    value: UserPreferences[K]
  ): boolean {
    const preferences = this.getPreferences();
    preferences[key] = value;
    return this.setPreferences(preferences);
  }

  // ===========================================================================
  // OAuth State (CSRF Protection)
  // ===========================================================================

  /**
   * Gets the OAuth state for CSRF verification.
   *
   * @returns OAuth state string or null
   */
  static getOAuthState(): string | null {
    try {
      return localStorage.getItem(STORAGE_KEYS.OAUTH_STATE);
    } catch {
      return null;
    }
  }

  /**
   * Saves OAuth state for CSRF verification.
   *
   * @param state - State string to save
   */
  static setOAuthState(state: string): void {
    try {
      localStorage.setItem(STORAGE_KEYS.OAUTH_STATE, state);
    } catch (error) {
      console.error('Failed to save OAuth state:', error);
    }
  }

  /**
   * Clears OAuth state after verification.
   */
  static clearOAuthState(): void {
    try {
      localStorage.removeItem(STORAGE_KEYS.OAUTH_STATE);
    } catch (error) {
      console.error('Failed to clear OAuth state:', error);
    }
  }

  // ===========================================================================
  // Guest User ID (Server-Side Session Recovery)
  // ===========================================================================

  /**
   * Gets the server-side guest user ID for session recovery.
   *
   * This ID is used to recover a guest session when the session cookie expires.
   *
   * @returns Guest user ID or null if not set
   */
  static getGuestUserId(): string | null {
    try {
      return localStorage.getItem(STORAGE_KEYS.GUEST_USER_ID);
    } catch {
      return null;
    }
  }

  /**
   * Saves the server-side guest user ID for session recovery.
   *
   * @param userId - Server-side user ID to save
   */
  static setGuestUserId(userId: string): void {
    try {
      localStorage.setItem(STORAGE_KEYS.GUEST_USER_ID, userId);
    } catch (error) {
      console.error('Failed to save guest user ID:', error);
    }
  }

  /**
   * Clears the guest user ID.
   *
   * Call this when:
   * - Guest upgrades to authenticated user
   * - Guest recovery fails with GUEST_UPGRADED error
   */
  static clearGuestUserId(): void {
    try {
      localStorage.removeItem(STORAGE_KEYS.GUEST_USER_ID);
    } catch (error) {
      console.error('Failed to clear guest user ID:', error);
    }
  }

  /**
   * Checks if a guest user ID exists.
   *
   * @returns True if guest user ID is stored
   */
  static hasGuestUserId(): boolean {
    return this.getGuestUserId() !== null;
  }

  // ===========================================================================
  // Character Storage (Primary Game Data)
  // ===========================================================================

  /**
   * Gets all characters from localStorage.
   *
   * @returns Array of CharacterData, empty array if none exist
   */
  static getCharacters(): CharacterData[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.CHARACTERS);
      if (!raw) {
        return [];
      }

      const parsed = JSON.parse(raw) as unknown;

      if (!Array.isArray(parsed)) {
        console.warn('Invalid characters data in localStorage, returning empty array');
        return [];
      }

      // Filter out invalid characters
      const validCharacters = parsed.filter((character) => validateCharacterData(character));

      // Sort by slot index
      return validCharacters.sort((a, b) => a.slotIndex - b.slotIndex);
    } catch (error) {
      console.error('Failed to read characters:', error);
      return [];
    }
  }

  /**
   * Saves all characters to localStorage.
   *
   * @param characters - Array of characters to save
   * @returns True if save succeeded, false otherwise
   */
  static setCharacters(characters: CharacterData[]): boolean {
    try {
      localStorage.setItem(STORAGE_KEYS.CHARACTERS, JSON.stringify(characters));
      return true;
    } catch (error) {
      console.error('Failed to save characters:', error);
      return false;
    }
  }

  /**
   * Gets a single character by ID.
   *
   * @param id - Character ID to find
   * @returns CharacterData if found, null otherwise
   */
  static getCharacter(id: string): CharacterData | null {
    const characters = this.getCharacters();
    return characters.find((character) => character.id === id) ?? null;
  }

  /**
   * Upserts a character (insert or update).
   *
   * If character exists (by ID), updates it. Otherwise, inserts it.
   *
   * @param character - Character to upsert
   * @returns True if save succeeded, false otherwise
   */
  static upsertCharacter(character: CharacterData): boolean {
    const characters = this.getCharacters();
    const existingIndex = characters.findIndex((c) => c.id === character.id);

    if (existingIndex >= 0) {
      characters[existingIndex] = character;
    } else {
      characters.push(character);
    }

    return this.setCharacters(characters);
  }

  /**
   * Deletes a character by ID.
   *
   * @param id - Character ID to delete
   * @returns True if character was deleted, false if not found or error
   */
  static deleteCharacter(id: string): boolean {
    const characters = this.getCharacters();
    const filteredCharacters = characters.filter((character) => character.id !== id);

    if (filteredCharacters.length === characters.length) {
      // Character not found
      return false;
    }

    // If deleted character was active, clear active ID
    if (this.getActiveCharacterId() === id) {
      this.setActiveCharacterId(null);
    }

    return this.setCharacters(filteredCharacters);
  }

  /**
   * Gets the active character ID.
   *
   * @returns Active character ID or null if not set
   */
  static getActiveCharacterId(): string | null {
    try {
      return localStorage.getItem(STORAGE_KEYS.ACTIVE_CHARACTER_ID);
    } catch {
      return null;
    }
  }

  /**
   * Sets the active character ID.
   *
   * @param id - Character ID to set as active, or null to clear
   */
  static setActiveCharacterId(id: string | null): void {
    try {
      if (id === null) {
        localStorage.removeItem(STORAGE_KEYS.ACTIVE_CHARACTER_ID);
      } else {
        localStorage.setItem(STORAGE_KEYS.ACTIVE_CHARACTER_ID, id);
      }
    } catch (error) {
      console.error('Failed to set active character ID:', error);
    }
  }

  /**
   * Gets the active character.
   *
   * @returns Active CharacterData or null if not set or not found
   */
  static getActiveCharacter(): CharacterData | null {
    const activeId = this.getActiveCharacterId();
    if (!activeId) {
      return null;
    }
    return this.getCharacter(activeId);
  }

  /**
   * Gets characters that need to be synced to server.
   *
   * @returns Array of characters with needsSync = true
   */
  static getCharactersNeedingSync(): CharacterData[] {
    return this.getCharacters().filter((character) => character.needsSync);
  }

  /**
   * Marks a character as synced.
   *
   * Updates the character's lastSyncedAt and sets needsSync to false.
   *
   * @param id - Character ID to mark as synced
   * @param syncedAt - Timestamp of sync (usually from server)
   * @returns True if character was updated, false if not found
   */
  static markCharacterSynced(id: string, syncedAt: number): boolean {
    const character = this.getCharacter(id);
    if (!character) {
      return false;
    }

    character.lastSyncedAt = syncedAt;
    character.needsSync = false;

    return this.upsertCharacter(character);
  }

  /**
   * Gets the number of characters stored.
   *
   * @returns Character count
   */
  static getCharacterCount(): number {
    return this.getCharacters().length;
  }

  /**
   * Finds the next available slot index.
   *
   * @returns Next available slot index (0-based)
   */
  static findNextSlotIndex(): number {
    const characters = this.getCharacters();
    if (characters.length === 0) {
      return 0;
    }

    // Find the first gap or use the next number
    const usedSlots = new Set(characters.map((character) => character.slotIndex));
    for (let i = 0; i <= characters.length; i++) {
      if (!usedSlots.has(i)) {
        return i;
      }
    }

    return characters.length;
  }

  /**
   * Gets approximate storage usage for character data.
   *
   * @returns Size in bytes
   */
  static getCharacterStorageUsage(): number {
    try {
      const value = localStorage.getItem(STORAGE_KEYS.CHARACTERS);
      if (value) {
        // Approximate: 2 bytes per character in UTF-16
        return (STORAGE_KEYS.CHARACTERS.length + value.length) * 2;
      }
    } catch {
      // Ignore errors
    }

    return 0;
  }

  // ===========================================================================
  // Tick Time (Offline Progress Tracking - Per Character)
  // ===========================================================================

  /**
   * Gets the storage key for a character's last tick time.
   *
   * @param characterId - The character ID
   * @returns The storage key
   */
  private static getTickTimeKey(characterId: string): string {
    return `${STORAGE_KEYS.LAST_TICK_TIME}:${characterId}`;
  }

  /**
   * Gets the last tick timestamp for a character.
   *
   * Used to calculate offline progress when the game resumes.
   *
   * @param characterId - The character ID to get tick time for
   * @returns Unix timestamp in milliseconds, or null if never set
   */
  static getLastTickTime(characterId: string): number | null {
    try {
      const key = this.getTickTimeKey(characterId);
      const value = localStorage.getItem(key);
      if (!value) {
        return null;
      }
      const timestamp = parseInt(value, 10);
      return Number.isNaN(timestamp) ? null : timestamp;
    } catch {
      return null;
    }
  }

  /**
   * Sets the last tick timestamp for a character.
   *
   * Called periodically while the game is running to track when
   * the character was last active.
   *
   * @param characterId - The character ID to set tick time for
   * @param timestamp - Unix timestamp in milliseconds
   */
  static setLastTickTime(characterId: string, timestamp: number): void {
    try {
      const key = this.getTickTimeKey(characterId);
      localStorage.setItem(key, timestamp.toString());
    } catch (error) {
      console.error('Failed to save last tick time:', error);
    }
  }

  /**
   * Clears the last tick timestamp for a character.
   *
   * Call this when the character is deleted.
   *
   * @param characterId - The character ID to clear tick time for
   */
  static clearLastTickTime(characterId: string): void {
    try {
      const key = this.getTickTimeKey(characterId);
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Failed to clear last tick time:', error);
    }
  }

  /**
   * Clears all tick timestamps for all characters.
   *
   * Used when resetting the game.
   */
  static clearAllTickTimes(): void {
    try {
      const prefix = STORAGE_KEYS.LAST_TICK_TIME + ':';
      const keysToRemove: string[] = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(prefix)) {
          keysToRemove.push(key);
        }
      }

      for (const key of keysToRemove) {
        localStorage.removeItem(key);
      }
    } catch (error) {
      console.error('Failed to clear all tick times:', error);
    }
  }

  // ===========================================================================
  // Utility Methods
  // ===========================================================================

  /**
   * Clears all Idle Depths data from localStorage.
   *
   * Use with caution - this removes all user data.
   */
  static clearAll(): void {
    try {
      Object.values(STORAGE_KEYS).forEach((key) => {
        localStorage.removeItem(key);
      });
    } catch (error) {
      console.error('Failed to clear localStorage:', error);
    }
  }

  /**
   * Generates a unique guest ID.
   *
   * @returns UUID v4 string
   */
  static generateGuestId(): string {
    return crypto.randomUUID();
  }

  /**
   * Creates initial guest data with defaults.
   *
   * @returns New GuestData object
   */
  static createGuestData(): GuestData {
    return {
      guestId: this.generateGuestId(),
      createdAt: Date.now(),
      preferences: { ...DEFAULT_PREFERENCES },
    };
  }

  /**
   * Gets or creates guest data.
   *
   * If guest data exists, returns it. Otherwise creates and saves new guest data.
   *
   * @returns Existing or new GuestData
   */
  static getOrCreateGuestData(): GuestData {
    const existing = this.getGuestData();
    if (existing) {
      return existing;
    }

    const newData = this.createGuestData();
    this.setGuestData(newData);
    return newData;
  }

  /**
   * Checks if localStorage is available.
   *
   * @returns True if localStorage is available and writable
   */
  static isAvailable(): boolean {
    try {
      const testKey = `${KEY_PREFIX}test`;
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Gets approximate storage usage for Idle Depths data.
   *
   * @returns Size in bytes
   */
  static getStorageUsage(): number {
    let totalSize = 0;

    try {
      Object.values(STORAGE_KEYS).forEach((key) => {
        const value = localStorage.getItem(key);
        if (value) {
          // Approximate: 2 bytes per character in UTF-16
          totalSize += (key.length + value.length) * 2;
        }
      });
    } catch {
      // Ignore errors
    }

    return totalSize;
  }
}
